import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { useParseTransactionFilterWithAI } from "@/hooks/api/useTransactions";
import { cn } from "@/lib/utils";
import type { Account, Category, TransactionFilterAI, TransactionFilters } from "@/types";
import { ArrowRight, Loader2, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface AITransactionFilterProps {
  onFilterApply: (filters: TransactionFilters) => void;
  accounts: Account[];
  categories: Category[];
}

export function AITransactionFilter({
  onFilterApply,
  accounts,
  categories,
}: AITransactionFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [parsedFilter, setParsedFilter] = useState<TransactionFilterAI | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: parseFilter, isPending } = useParseTransactionFilterWithAI();

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    parseFilter(query, {
      onSuccess: (data) => {
        setParsedFilter(data);
      },
      onError: (error) => {
        console.error("Failed to parse filter:", error);
      },
    });
  };

  const handleApplyFilter = () => {
    if (!parsedFilter) return;

    // Look up accountId from accountName if needed
    let accountId = parsedFilter.accountId;
    if (!accountId && parsedFilter.accountName) {
      const account = accounts.find(
        (a) => a.accountName.toLowerCase() === parsedFilter.accountName?.toLowerCase(),
      );
      accountId = account?.id;
    }

    // Look up categoryId from categoryName if needed
    let categoryId = parsedFilter.categoryId;
    if (!categoryId && parsedFilter.categoryName) {
      const category = categories.find(
        (c) => c.name.toLowerCase() === parsedFilter.categoryName?.toLowerCase(),
      );
      categoryId = category?.id;
    }

    // Filter out "transfer" type as backend doesn't support it yet
    const transactionType = parsedFilter.type === "transfer" ? undefined : parsedFilter.type;

    const filters: TransactionFilters = {
      startDate: parsedFilter.startDate,
      endDate: parsedFilter.endDate,
      accountId,
      categoryId,
      type: transactionType,
      isReviewed: parsedFilter.isReviewed,
      minAmount: parsedFilter.minAmount,
      maxAmount: parsedFilter.maxAmount,
      notes: parsedFilter.searchText, // Map searchText to notes parameter
    };

    onFilterApply(filters);
    handleClear();
  };

  const handleClear = () => {
    setQuery("");
    setParsedFilter(null);
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(true)}
        className="h-7 text-xs"
      >
        <Sparkles className="size-3.5" />
        AI Filter
      </Button>
    );
  }

  return (
    <div className="flex-1 min-w-[280px] max-w-xl">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g., expenses over ₹1000 from last month"
            className={cn("h-9 text-xs pr-8", isPending && "opacity-50")}
            disabled={isPending}
          />
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <Button type="submit" size="sm" disabled={!query.trim() || isPending} className="h-9 px-3">
          {isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <ArrowRight className="size-3.5" />
          )}
        </Button>
      </form>

      {parsedFilter && (
        <div className="mt-3 space-y-2">
          <div className="flex items-start gap-2 p-2 text-xs bg-muted rounded-md">
            <Sparkles className="size-3.5 mt-0.5 flex-shrink-0 text-primary" />
            <span className="text-muted-foreground">{parsedFilter.explanation}</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {parsedFilter.startDate && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">From:</span>
                <span>{parsedFilter.startDate}</span>
              </div>
            )}
            {parsedFilter.endDate && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">To:</span>
                <span>{parsedFilter.endDate}</span>
              </div>
            )}
            {parsedFilter.type && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">Type:</span>
                <span className="capitalize">{parsedFilter.type}</span>
              </div>
            )}
            {parsedFilter.minAmount !== undefined && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">Min:</span>
                <span>
                  <AmountDisplay amount={parsedFilter.minAmount} />
                </span>
              </div>
            )}
            {parsedFilter.maxAmount !== undefined && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">Max:</span>
                <span>
                  <AmountDisplay amount={parsedFilter.maxAmount} />
                </span>
              </div>
            )}
            {parsedFilter.categoryName && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">Category:</span>
                <span>{parsedFilter.categoryName}</span>
              </div>
            )}
            {parsedFilter.accountName && (
              <div className="inline-flex items-center gap-1 h-7 px-2 rounded bg-accent text-xs">
                <span className="text-primary font-medium">Account:</span>
                <span>{parsedFilter.accountName}</span>
              </div>
            )}
            {parsedFilter.isReviewed !== undefined && (
              <div className="inline-flex items-center h-7 px-2 rounded bg-accent text-xs">
                {parsedFilter.isReviewed ? "Reviewed" : "Unreviewed"}
              </div>
            )}
          </div>

          <Button size="sm" onClick={handleApplyFilter} className="h-8 w-full text-xs">
            Apply Filter
          </Button>
        </div>
      )}
    </div>
  );
}
